/**
 * Color handling for Globo collection cards.
 *
 * Globo renders its own grid from snippets/globo.filter.product.liquid, so the theme's
 * "Show one card per color" setting (data-split-cards-by-color on <results-list>) never
 * reaches it. This script applies the setting on the client:
 * - Off: one card per product with a row of color photo buttons.
 * - On: each Globo card is cloned into one card per color.
 *
 * Globo usually sends one product with every color. Some Globo setups split a product into
 * one entry per color instead; those siblings share a product id and are merged here.
 */

const globoGridSelector = '#gf-products, #gf-grid';
const globoCardSelector = '#gf-products product-card[data-product-id], #gf-grid product-card[data-product-id]';
const maxButtons = 4;
const maxSlides = 5;
const cardImageSizes = '(min-width: 750px) 25vw, 50vw';

/**
 * @param {string} value
 */
const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * @param {string} src
 * @param {number} width
 */
const sizedImage = (src, width) => {
  if (!src) return '';

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
};

/**
 * @param {object} root
 */
const findProducts = (root) => {
  let best = [];
  const seen = new WeakSet();

  const visit = (obj, depth) => {
    if (!obj || typeof obj !== 'object' || depth > 7 || seen.has(obj)) return;
    seen.add(obj);

    if (Array.isArray(obj)) {
      const first = obj[0];
      if (
        obj.length > best.length &&
        first &&
        first.id &&
        Array.isArray(first.variants) &&
        Array.isArray(first.images)
      ) {
        best = obj;
      }

      for (const item of obj) visit(item, depth + 1);
      return;
    }

    for (const value of Object.values(obj)) visit(value, depth + 1);
  };

  if (root) visit(root, 0);
  return best;
};

/**
 * @param {object} variant
 * @param {number} index
 */
const optionValue = (variant, index) => {
  const selected = variant.selected_options && variant.selected_options[index];
  if (selected && selected.value != null) return String(selected.value);

  const legacy = variant[`option${index + 1}`];
  return legacy == null ? '' : String(legacy);
};

/**
 * @param {object} product
 * @param {object[]} variants
 */
const heroImageFor = (product, variants) => {
  const images = product.images || [];
  const ids = new Set(variants.map((variant) => String(variant.id)));

  for (const variant of variants) {
    if (variant.image_id == null) continue;
    const match = images.find((image) => String(image.id) === String(variant.image_id));
    if (match) return match;
  }

  const tagged = images.find((image) => (image.variant_ids || []).some((id) => ids.has(String(id))));
  return tagged || product.image || product.featured_image || images[0] || null;
};

/**
 * @param {object} product
 */
const colorsOf = (product) => {
  const options = product.options || [];
  const index = options.findIndex((option) => /^colou?r$/i.test(option.name));
  if (index === -1) return [];

  const variants = product.variants || [];
  const colors = [];

  for (const value of options[index].values || []) {
    const name = String(value);
    const matching = variants.filter((variant) => optionValue(variant, index) === name);
    // Split Globo entries list one color but may still carry every variant; skip empty colors.
    if (variants.length && !matching.length) continue;

    const availableVariant = matching.find((variant) => variant.available) || matching[0];
    const hero = heroImageFor(product, matching);
    // Globo's color filter matches a color family ("Delta Blue" is "Blue"). Globo sometimes sends it
    // as the option after the real ones; it is not always there, so the name is matched by word too.
    const families = new Set([name]);
    if (options.length < 3) {
      for (const variant of matching) {
        const family = variant[`option${options.length + 1}`];
        if (family) families.add(String(family));
      }
    }

    colors.push({
      name,
      product,
      hero,
      variantId: availableVariant ? String(availableVariant.id) : '',
      variantIds: matching.map((variant) => String(variant.id)),
      families,
      available: matching.length ? matching.some((variant) => variant.available) : product.available !== false,
      mediaId: hero ? String(hero.id) : '',
    });
  }

  return colors;
};

/**
 * @param {object} product
 * @param {object | null} hero
 */
const colorwayImages = (product, hero) => {
  const images = product.images || [];
  if (!hero) return images.slice(0, 1);

  const heroIds = new Set((hero.variant_ids || []).map(String));
  const slides = [];
  let seenHero = false;

  for (const image of images) {
    if (slides.length >= maxSlides) break;

    if (!seenHero) {
      if (image.src === hero.src || String(image.id) === String(hero.id)) {
        seenHero = true;
        slides.push(image);
      }
      continue;
    }

    // Images after a color's photo belong to it until the next color-tagged photo.
    const variantIds = image.variant_ids || [];
    const sharesHero = variantIds.length === 0 || variantIds.some((id) => heroIds.has(String(id)));

    if (!sharesHero) break;
    slides.push(image);
  }

  if (!slides.length) slides.push(hero);
  return slides;
};

/** @type {Map<string, ReturnType<typeof colorsOf>>} */
const groups = new Map();

/**
 * Merge colors by product id. Load more appends pages, so earlier colors are kept.
 *
 * @param {object[]} products
 */
const mergeProducts = (products) => {
  for (const product of products) {
    const id = String(product.id);
    const merged = groups.get(id) || [];

    for (const color of colorsOf(product)) {
      const index = merged.findIndex((entry) => entry.name === color.name);
      if (index === -1) merged.push(color);
      else merged[index] = color;
    }

    groups.set(id, merged);
  }
};

/**
 * @param {Element} card
 */
const splitByColor = (card) => card.closest('[data-split-cards-by-color="true"]') !== null;

/**
 * @param {Element} card
 */
const variantIdOf = (card) => {
  const link = card.querySelector('a[href*="variant="]');
  if (!(link instanceof HTMLAnchorElement)) return '';

  try {
    return new URL(link.href, window.location.origin).searchParams.get('variant') || '';
  } catch {
    return '';
  }
};

/**
 * @param {HTMLElement} card
 * @param {{ name: string, variantIds: string[] }[]} colors
 */
const currentColorName = (card, colors) => {
  if (card.dataset.gfColor) return card.dataset.gfColor;

  const variantId = variantIdOf(card);
  const match = variantId && colors.find((color) => color.variantIds.includes(variantId));
  if (match) return match.name;

  const title = card.querySelector('[ref="productTitleLink"] p, [ref="productTitleLink"] .title-text');
  const text = title ? title.textContent.trim() : '';
  const fromTitle = colors.find((color) => text.endsWith(` - ${color.name}`) || text.endsWith(` | ${color.name}`));
  return fromTitle ? fromTitle.name : colors[0].name;
};

/**
 * @param {string} title
 * @param {string} color
 */
const baseTitle = (title, color) => {
  const suffixes = [` - ${color}`, ` | ${color}`];

  for (const suffix of suffixes) {
    if (title.endsWith(suffix)) return title.slice(0, -suffix.length);
  }

  return title;
};

/**
 * @param {HTMLElement} card
 */
const titleOf = (card) => {
  const node = card.querySelector('[ref="productTitleLink"] p, [ref="productTitleLink"] .title-text');
  return node ? node.textContent.trim() : '';
};

/**
 * @param {{ name: string, available: boolean, variantId: string, mediaId: string, hero: object | null }} color
 * @param {boolean} selected
 * @param {string} productTitle
 * @param {number} index
 */
const buttonMarkup = (color, selected, productTitle, index) => {
  const src = color.hero && color.hero.src ? sizedImage(color.hero.src, 80) : '';
  const hideOnMobile = index >= 3 ? ' variant-image-button--hide-on-mobile' : '';
  const selectedClass = selected ? ' selected' : '';
  const image = src
    ? `<img src="${src}" alt="${escapeHtml(`${color.name} - ${productTitle}`)}" class="variant-image-button__image" loading="lazy" width="80" height="80">`
    : `<div class="variant-image-button__placeholder"><span class="variant-image-button__placeholder-text">${escapeHtml(color.name.slice(0, 2).toUpperCase())}</span></div>`;

  return `<div class="variant-image-button variant-image-button--small product-variant-images__button${hideOnMobile}">
    <button type="button" class="variant-image-button__button${selectedClass}" title="${escapeHtml(color.name)}" data-variant-id="${escapeHtml(color.variantId)}" data-option-value="${escapeHtml(color.name)}" data-color-name="${escapeHtml(color.name)}" data-media-id="${escapeHtml(color.mediaId)}" aria-label="${escapeHtml(color.name)}" aria-pressed="${selected ? 'true' : 'false'}" ${selected ? 'data-selected="true"' : ''} ${color.available ? '' : 'disabled'}>
      ${image}
      ${color.available ? '' : '<span class="variant-image-button__unavailable" aria-hidden="true"></span>'}
    </button>
  </div>`;
};

/**
 * @param {{ name: string }[]} colors
 */
const moreMarkup = (colors) => {
  const remaining = colors.length - maxButtons;
  const remainingMobile = colors.length - 3;

  if (remaining > 0) {
    return `<div class="variant-images__more" title="+${remaining} more variants" data-remaining-count="${remaining}" data-remaining-count-mobile="${remainingMobile}">
      <span class="variant-images__more-count">
        <span class="variant-images__more-count--desktop">+${remaining}</span>
        <span class="variant-images__more-count--mobile" aria-hidden="true">+${remainingMobile}</span>
      </span>
    </div>`;
  }

  if (remainingMobile > 0) {
    return `<div class="variant-images__more variant-images__more--mobile-only" title="+${remainingMobile} more variants" data-remaining-count-mobile="${remainingMobile}">
      <span class="variant-images__more-count">+${remainingMobile}</span>
    </div>`;
  }

  return '';
};

/**
 * @param {HTMLElement} card
 */
const existingRow = (card) =>
  card.querySelector(':scope > .product-variant-images, .product-card__content > .product-variant-images');

/**
 * @param {HTMLElement} card
 * @param {ReturnType<typeof colorsOf> | undefined} colors
 */
const renderRow = (card, colors) => {
  const existing = existingRow(card);

  if (!colors || colors.length < 2) {
    existing?.remove();
    return;
  }

  if (existing && existing.getAttribute('data-color-count') === String(colors.length)) return;
  if (existing) existing.remove();

  const selectedName = currentColorName(card, colors);
  const selected = colors.find((color) => color.name === selectedName) || colors[0];
  const title = titleOf(card) || selected.product.title || '';
  const productTitle = baseTitle(title, selected.name);
  const shown = colors.slice(0, maxButtons);

  const row = document.createElement('div');
  row.className = 'product-variant-images';
  row.setAttribute('data-color-count', String(colors.length));
  // Only rewrite titles on cards whose title already names a color.
  if (productTitle !== title) row.setAttribute('data-base-title', productTitle);
  row.innerHTML = `<div class="variant-image-buttons-group variant-image-buttons-group--start">
    ${shown.map((color, index) => buttonMarkup(color, color.name === selected.name, productTitle, index)).join('')}
    ${moreMarkup(colors)}
  </div>`;

  const price = card.querySelector('product-price');
  if (price && price.parentElement) {
    price.after(row);
  } else {
    card.append(row);
  }
};

/**
 * @param {object} image
 * @param {number} index
 */
const slideMarkup = (image, index) => {
  const width = Number(image.width) || 1200;
  const height = Number(image.height) || width;
  const ratio = Number(image.aspect_ratio) || width / height || 1;
  const displayHeight = Math.round(1200 / ratio);
  const srcset = [240, 352, 832, 1200, 1600, 1920, 2560, 3840]
    .map((size) => `${sizedImage(image.src, size)} ${size}w`)
    .join(', ');

  return `<slideshow-slide ref="slides[]" aria-hidden="${index === 0 ? 'false' : 'true'}" style="view-timeline-name: --slide-${index + 1}; --product-media-fit: cover;" class="product-media-container media-fit product-media-container--image" variant-image="" slide-id="${escapeHtml(image.id)}">
    <div class="product-media" style="--ratio: ${ratio}" data-media-id="${escapeHtml(image.id)}">
      <img src="${sizedImage(image.src, 1200)}" alt="${escapeHtml(image.alt || '')}" srcset="${srcset}" width="1200" height="${displayHeight}" ${index === 0 ? '' : 'loading="lazy"'} sizes="${cardImageSizes}" class="product-media__image" fetchpriority="${index === 0 ? 'high' : 'auto'}" style="--focal-point: 50.0% 50.0%;">
    </div>
  </slideshow-slide>`;
};

/**
 * @param {HTMLElement} card
 * @param {string} variantId
 */
const updateVariant = (card, variantId) => {
  if (!variantId) return;

  for (const link of card.querySelectorAll('a[href]')) {
    if (!(link instanceof HTMLAnchorElement) || !link.href.includes('/products/')) continue;

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', variantId);
      link.href = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      // Ignore malformed card links.
    }
  }

  // Quick add submits this hidden input.
  for (const input of card.querySelectorAll('input[name="id"]')) {
    if (input instanceof HTMLInputElement) input.value = variantId;
  }
};

/**
 * @param {HTMLElement} card
 * @param {string} title
 */
const updateTitles = (card, title) => {
  const nodes = card.querySelectorAll(
    '[ref="productTitleLink"] p, [ref="productTitleLink"] .title-text, .product-card__link .visually-hidden, .card-gallery a.contents'
  );

  for (const node of nodes) {
    if (node.classList.contains('contents')) {
      node.setAttribute('aria-label', title);
      continue;
    }

    node.textContent = title;
  }
};

/**
 * @param {HTMLElement} card
 * @param {{ product: object, hero: object | null }} color
 */
const swapGallery = (card, color) => {
  const slideshow = card.querySelector('slideshow-component');
  const scroller = card.querySelector('slideshow-slides');
  const images = colorwayImages(color.product, color.hero);
  if (!slideshow || !scroller || !images.length) return;

  scroller.innerHTML = images.map((image, index) => slideMarkup(image, index)).join('');

  if (images.length > 1) slideshow.setAttribute('infinite', '');
  else slideshow.removeAttribute('infinite');

  slideshow.setAttribute('data-generic-media-size', String(images.length));

  const arrows = slideshow.querySelector('slideshow-arrows');
  if (arrows instanceof HTMLElement) arrows.style.display = images.length < 2 ? 'none' : '';

  const firstId = String(images[0].id);

  queueMicrotask(() => {
    scroller.scrollLeft = 0;

    if (typeof slideshow.select === 'function') {
      slideshow.select({ id: firstId }, undefined, { animate: false });
    }
  });
};

/**
 * @param {HTMLElement} button
 */
const selectColor = (button) => {
  const card = button.closest('product-card');
  if (!(card instanceof HTMLElement)) return;

  const colorName = button.dataset.colorName;
  if (!colorName) return;

  const colors = groups.get(card.dataset.productId || '');
  const color = colors && colors.find((entry) => entry.name === colorName);
  if (!color) return;

  const group = button.closest('.variant-image-buttons-group');
  if (!group) return;

  for (const sibling of group.querySelectorAll('.variant-image-button__button')) {
    sibling.classList.remove('selected');
    sibling.setAttribute('aria-pressed', 'false');
    sibling.removeAttribute('data-selected');
  }

  button.classList.add('selected');
  button.setAttribute('aria-pressed', 'true');
  button.setAttribute('data-selected', 'true');

  const row = button.closest('.product-variant-images');
  const base = row ? row.getAttribute('data-base-title') || '' : '';
  if (base) updateTitles(card, `${base} - ${colorName}`);

  updateVariant(card, color.variantId);
  swapGallery(card, color);
};

/**
 * @param {HTMLElement} card
 * @param {ReturnType<typeof colorsOf>[number]} color
 * @param {string} base
 */
const showColor = (card, color, base) => {
  card.dataset.gfColor = color.name;
  updateTitles(card, `${base} | ${color.name}`);
  updateVariant(card, color.variantId);
  swapGallery(card, color);
};

/**
 * Values of Globo's active color filter. Globo redraws the grid before it updates the URL
 * (?filter.color=Blue), so the checked filter buttons are read too.
 */
const activeColorFilters = () => {
  const fromUrl = new URLSearchParams(window.location.search).getAll('filter.color').flatMap((value) => value.split(','));
  const fromButtons = [
    ...document.querySelectorAll('.gf-option-block[data-filter-type="color"] [aria-checked="true"][data-fvalue]'),
  ].map((button) => button.getAttribute('data-fvalue') || '');
  const values = [...fromUrl, ...fromButtons].map((value) => value.trim().toLowerCase()).filter(Boolean);

  return new Set(values);
};

/**
 * @param {string} value
 */
const words = (value) => value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/**
 * @param {ReturnType<typeof colorsOf>} colors
 * @param {string} current Color Globo picked for this card, which already matches the filter.
 */
const filteredColors = (colors, current) => {
  const active = activeColorFilters();
  if (!active.size) return colors;

  const activeWords = new Set([...active].flatMap(words));
  const matches = colors.filter(
    (color) =>
      color.name === current ||
      [...color.families].some((family) => active.has(family.toLowerCase())) ||
      words(color.name).some((word) => activeWords.has(word))
  );

  return matches.length ? matches : colors;
};

/**
 * "Show one card per color" on: clone each multi-color Globo card into one card per color,
 * matching the theme's own split cards ("Title | Color").
 *
 * @param {HTMLElement} card
 * @param {ReturnType<typeof colorsOf> | undefined} colors
 */
const splitCard = (card, colors) => {
  existingRow(card)?.remove();

  const item = card.closest('li');
  if (!(item instanceof HTMLElement) || item.hasAttribute('data-gf-color-split')) return;
  // Single-color products stay as they are, like the theme's own split.
  if (!colors || colors.length < 2) return;

  // With a color filter on, only the matching colors get a card.
  const current = currentColorName(card, colors);
  const shown = filteredColors(colors, current);

  item.setAttribute('data-gf-color-split', '');

  const first = shown.some((color) => color.name === current) ? current : shown[0].name;
  const ordered = [...shown].sort((a, b) => Number(b.name === first) - Number(a.name === first));
  const base = baseTitle(titleOf(card), current);
  let previous = item;

  ordered.forEach((color, index) => {
    const target = index === 0 ? item : /** @type {HTMLElement} */ (item.cloneNode(true));

    if (index > 0) {
      target.id = `${item.id}-${color.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      previous.after(target);
    }

    const targetCard = target.querySelector('product-card');
    if (targetCard instanceof HTMLElement) showColor(targetCard, color, base);
    previous = target;
  });
};

/**
 * Some Globo setups send one entry per color, so sibling cards share a product id. When
 * splitting is off, keep the first card for each product; its row shows every color.
 *
 * @param {Element[]} cards
 */
const hideSiblingCards = (cards) => {
  const kept = new Set();

  for (const card of cards) {
    const item = card.closest('li') || card;
    if (!(item instanceof HTMLElement)) continue;

    const id = card.getAttribute('data-product-id') || '';
    const duplicate =
      !splitByColor(card) &&
      !card.hasAttribute('data-card-color') &&
      !item.hasAttribute('data-gf-color-split') &&
      kept.has(id);

    item.classList.toggle('gf-color-sibling-hidden', duplicate);
    if (!duplicate) kept.add(id);
  }
};

const apply = () => {
  mergeProducts(findProducts(window.globofilter));

  // A comma in the middle of a selector is OR, not "both grids". Writing
  // `${globoGridSelector} product-card` becomes `#gf-products, #gf-grid product-card`.
  const cards = [...document.querySelectorAll(globoCardSelector)];
  hideSiblingCards(cards);

  for (const card of cards) {
    // Cards with data-card-color are the theme's own split render; leave them alone.
    if (!(card instanceof HTMLElement) || card.hasAttribute('data-card-color')) continue;

    const colors = groups.get(card.dataset.productId || '');

    if (splitByColor(card)) splitCard(card, colors);
    else renderRow(card, colors);
  }
};

let frame = 0;

const schedule = () => {
  if (frame) return;

  frame = requestAnimationFrame(() => {
    frame = 0;
    apply();
  });
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  // closest('#gf-products, #gf-grid .variant-image-button__button') matches #gf-products
  // itself, so every click in the grid was treated as a color button and cancelled.
  const button = event.target.closest('.variant-image-button__button');
  if (!(button instanceof HTMLElement) || button.disabled) return;
  if (!button.closest(globoGridSelector)) return;

  event.preventDefault();
  selectColor(button);
});

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      // Clones from splitCard carry data-gf-color-split; they don't need another pass.
      if (node.hasAttribute('data-gf-color-split')) continue;
      if (node.matches?.('product-card, #gf-products, #gf-grid') || node.querySelector?.('product-card')) {
        schedule();
        return;
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });
schedule();

for (const delay of [300, 1000, 2500]) {
  setTimeout(schedule, delay);
}

// The theme editor re-renders the collection section when a setting changes. That drops the
// filter and grid Globo injected, and Globo has no way to start again, so reload the preview.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element && event.target.querySelector('results-list')) {
    window.location.reload();
  }
});
